import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  min-height: 100vh;
  padding-top: 6rem;
  position: relative;
`;


const Section = styled.section`
  position: relative;
  padding: 4rem 1.5rem;
  background: ${({ theme }) => theme.colors.background};
`;

const SectionInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  gap: 3rem;
`;

const SectionHeader = styled.div`
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  text-align: center;
  margin: 0 auto;
`;

const Eyebrow = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.highlight};
`;

const SectionTitle = styled.h1`
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
`;

const SectionCopy = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  align-self: flex-start;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const NewsletterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const NewsletterCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: ${({ theme }) => theme.colors.cardShadow};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }
`;

const NewsletterHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const NewsletterIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #10B981, #047857);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
`;

const NewsletterInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NewsletterTitle = styled.h3`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  line-height: 1.3;
`;

const NewsletterMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const NewsletterDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

const StatusBadge = styled.span<{ status: 'published' | 'draft' | 'archived' }>`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ status }) => {
    switch (status) {
      case 'published': return '#D1FAE5';
      case 'draft': return '#FEF3C7';
      case 'archived': return '#E5E7EB';
      default: return '#E5E7EB';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'published': return '#065F46';
      case 'draft': return '#92400E';
      case 'archived': return '#4B5563';
      default: return '#4B5563';
    }
  }};
`;

const NewsletterActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: auto;
  padding-top: 1rem;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${({ variant, theme }) => 
    variant === 'secondary' ? 'transparent' : theme.colors.accent};
  color: ${({ variant, theme }) => 
    variant === 'secondary' ? theme.colors.accent : theme.colors.white};
  border: ${({ variant, theme }) => 
    variant === 'secondary' ? `1px solid ${theme.colors.accent}` : 'none'};
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;

  &:hover {
    background: ${({ variant, theme }) => 
      variant === 'secondary' ? theme.colors.accent : theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

interface Newsletter {
  id: string;
  title: string;
  description: string;
  filename: string;
  file_path: string;
  file_size: number;
  issue_number: string;
  publish_date: string;
  featured: boolean;
  category: string;
  tags: string[];
  download_count: number;
  status: 'draft' | 'published' | 'archived';
  created_at: string;
  updated_at: string;
}

const Newsletter: React.FC = () => {
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load newsletters from API
  useEffect(() => {
    loadNewsletters();
  }, []);

  const loadNewsletters = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/newsletters.php');
      const data = await response.json();
      
      if (data.success) {
        setNewsletters(data.newsletters);
      } else {
        setError(data.message || 'Failed to load newsletters');
      }
    } catch (err) {
      setError('Unable to connect to server. Please check your connection.');
      console.error('Error loading newsletters:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (newsletter: Newsletter) => {
    if (newsletter.status === 'draft') return;
    
    const viewUrl = `/api/download-newsletter.php?id=${newsletter.id}&action=view`;
    window.open(viewUrl, '_blank');
  };

  const handleDownload = (newsletter: Newsletter) => {
    if (newsletter.status !== 'published') return;
    
    const downloadUrl = `/api/download-newsletter.php?id=${newsletter.id}&action=download`;
    window.open(downloadUrl, '_blank');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <PageWrapper>
      <Section>
        <SectionInner>
          <BackButton to="/#resources">
            ← Back to Resources
          </BackButton>
          
          <SectionHeader>
            <Eyebrow>The Men's Well</Eyebrow>
            <SectionTitle>Newsletter Library</SectionTitle>
            <SectionCopy>
              Stay connected with our community through our regular newsletters featuring updates, wellness tips, and member stories.
            </SectionCopy>
          </SectionHeader>


          {/* Loading State */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📰</div>
              <h3>Loading Newsletters...</h3>
              <p>Please wait while we fetch the latest issues.</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div style={{ 
              background: '#fee2e2', 
              color: '#991b1b', 
              padding: '1.5rem', 
              borderRadius: '12px', 
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
              <h3>Unable to Load Newsletters</h3>
              <p>{error}</p>
              <button 
                onClick={loadNewsletters}
                style={{
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  marginTop: '1rem',
                  cursor: 'pointer'
                }}
              >
                Try Again
              </button>
            </div>
          )}

          {/* Content (only show when not loading and no error) */}
          {!loading && !error && (
            <>
              {newsletters.length === 0 ? (
                <EmptyState>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📰</div>
                  <h3>No Newsletters Available</h3>
                  <p>Check back soon for our latest newsletter issues!</p>
                </EmptyState>
              ) : (
                <NewsletterGrid>
                  {newsletters.map((newsletter) => (
                    <NewsletterCard key={newsletter.id}>
                      <NewsletterHeader>
                        <NewsletterIcon>
                          📰
                        </NewsletterIcon>
                        <NewsletterInfo>
                          <NewsletterTitle>{newsletter.title}</NewsletterTitle>
                          <NewsletterMeta>
                            <span><strong>Issue:</strong> {newsletter.issue_number}</span>
                            <span><strong>Date:</strong> {formatDate(newsletter.publish_date)}</span>
                            <span><strong>Size:</strong> {formatFileSize(newsletter.file_size)}</span>
                          </NewsletterMeta>
                        </NewsletterInfo>
                      </NewsletterHeader>
                      
                      <NewsletterDescription>
                        {newsletter.description}
                      </NewsletterDescription>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
                        <StatusBadge status={newsletter.status}>
                          {newsletter.status === 'published' ? 'Available' : 
                           newsletter.status === 'draft' ? 'Coming Soon' : 'Archived'}
                        </StatusBadge>
                        {newsletter.featured && (
                          <span style={{ 
                            fontSize: '0.8rem', 
                            color: '#10B981', 
                            fontWeight: '600' 
                          }}>
                            ⭐ Featured
                          </span>
                        )}
                        <span style={{ 
                          fontSize: '0.8rem', 
                          color: '#6B7280', 
                          marginLeft: 'auto' 
                        }}>
                          {newsletter.download_count} downloads
                        </span>
                      </div>
                      
                      <NewsletterActions>
                        <ActionButton 
                          onClick={() => handleView(newsletter)}
                          disabled={newsletter.status === 'draft'}
                        >
                          📖 View
                        </ActionButton>
                        
                        <ActionButton 
                          variant="secondary"
                          onClick={() => handleDownload(newsletter)}
                          disabled={newsletter.status !== 'published'}
                        >
                          📥 Download
                        </ActionButton>
                      </NewsletterActions>
                    </NewsletterCard>
                  ))}
                </NewsletterGrid>
              )}
            </>
          )}
        </SectionInner>
      </Section>
    </PageWrapper>
  );
};

export default Newsletter;